import { useState } from "react";
import { Outlet, useNavigate } from "react-router";

import { CircularProgress, Divider, Stack } from "@mui/material";

import { DutyScheduleDisplayStatus } from "@/api";
import { UserPermission } from "@/common/contexts/current-user-context/CurrentUserContextProvider.types";
import withAuthorization from "@/common/hocs/with-authorization/withAuthorization";
import { useRedirectWhenMissing } from "@/common/hooks/useRedirectWhenMissing";
import { DutyScheduleEditDialog } from "@/pages/duty-schedule/components/edit-dialog/DutyScheduleEditDialog";
import { useDutyScheduleCounselorOptions } from "@/pages/duty-schedule/hooks/useDutyScheduleCounselorOptions";
import { useDutyScheduleDelete } from "@/pages/duty-schedule/hooks/useDutyScheduleDelete";
import { useDutyScheduleDetail } from "@/pages/duty-schedule/hooks/useDutyScheduleDetail";
import { useDutyScheduleRelease } from "@/pages/duty-schedule/hooks/useDutyScheduleRelease";
import { AppRoutes } from "@/router/AppRoutes";
import { DutyScheduleTab } from "@/router/DutyScheduleRoutes";
import { useTranslation } from "@/translations";

import { DutyScheduleDetailContext } from "./DutyScheduleDetail.types";
import { DutyScheduleEditBar } from "./components/edit-bar/DutyScheduleEditBar";
import { DutyScheduleDetailHeader } from "./components/header/DutyScheduleDetailHeader";
import { DutyScheduleKeyFigures } from "./components/key-figures/DutyScheduleKeyFigures";
import { DutyScheduleDetailTabs } from "./components/tabs/DutyScheduleDetailTabs";
import { useActiveDutyScheduleTab } from "./hooks/useActiveDutyScheduleTab";
import { useDutyScheduleAssignments } from "./hooks/useDutyScheduleAssignments";

function DutyScheduleDetailInner() {
  const { t } = useTranslation();
  const { id, dutySchedule, isLoading } = useDutyScheduleDetail();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  // Held here rather than in the tab, which unmounts on every tab switch and would take them with it.
  const [selectedMonthKey, setSelectedMonthKey] = useState<string | null>(null);
  const [counselorFilter, setCounselorFilter] = useState<string[] | null>(null);

  const navigate = useNavigate();
  const { confirmAndDelete, isDeleting } = useDutyScheduleDelete({
    onDeleted: () => void navigate(AppRoutes.DutySchedule),
  });

  const activeTab = useActiveDutyScheduleTab();
  const { counselors } = useDutyScheduleCounselorOptions();
  const { resolveCell, assign, reset, save, isDirty, isSaving } = useDutyScheduleAssignments(
    id,
    dutySchedule,
    counselors
  );

  // The release saves the pending staffing edits after it is confirmed, which is what its
  // confirmation text promises: cancelling saves nothing, a failed save releases nothing.
  const { confirmAndRelease, isReleasing } = useDutyScheduleRelease({ onBeforeRelease: save });

  // A hand-typed or stale id, or a schedule deleted meanwhile, would otherwise leave a blank page
  // with no way back — the list is where there is something to do.
  useRedirectWhenMissing(isLoading, dutySchedule, AppRoutes.DutySchedule);

  if (isLoading) {
    return (
      <Stack alignItems="center" py={6}>
        <CircularProgress aria-label={t("dutySchedule.detail.loading")} />
      </Stack>
    );
  }

  if (dutySchedule == null || id == null) {
    return null;
  }

  // Once released a duty schedule is read-only in every respect, so the edit and delete actions go
  // away and the only thing left is looking at it.
  const isEditable = dutySchedule.status === DutyScheduleDisplayStatus.InProgress;
  const isDataEntry = activeTab === DutyScheduleTab.DataEntry;

  const context: DutyScheduleDetailContext = {
    dutySchedule,
    isEditable,
    selectedMonthKey,
    onSelectMonth: setSelectedMonthKey,
    counselorFilter,
    resolveCell,
    onAssign: assign,
  };

  return (
    <Stack spacing={2} flex={1} minHeight={0} overflow="hidden" pb={3}>
      <DutyScheduleDetailHeader
        dutySchedule={dutySchedule}
        isEditable={isEditable}
        isSaving={isSaving}
        isReleasing={isReleasing}
        isDeleting={isDeleting}
        onDelete={() => confirmAndDelete(dutySchedule)}
        onEdit={() => setIsEditDialogOpen(true)}
        onRelease={() => confirmAndRelease(dutySchedule)}
      />

      <DutyScheduleKeyFigures dutySchedule={dutySchedule} />

      <DutyScheduleDetailTabs
        id={id}
        activeTab={activeTab}
        dutySchedule={dutySchedule}
        counselors={counselors}
        counselorFilter={counselorFilter}
        onCounselorFilterChange={setCounselorFilter}
      />

      <Divider />

      <Stack flex={1} minHeight={0} overflow="hidden">
        <Outlet context={context} />
      </Stack>

      {isDataEntry && isEditable && (
        <DutyScheduleEditBar
          lastModifiedAtUtc={dutySchedule.lastModifiedAtUtc}
          lastModifiedByUserCode={dutySchedule.lastModifiedByUserCode}
          lastModifiedByName={dutySchedule.lastModifiedByName}
          isDirty={isDirty}
          isSaving={isSaving}
          onSave={() => void save()}
          onReset={reset}
        />
      )}

      {isEditDialogOpen && (
        <DutyScheduleEditDialog dutySchedule={dutySchedule} onClose={() => setIsEditDialogOpen(false)} />
      )}
    </Stack>
  );
}

export const DutyScheduleDetail = withAuthorization(DutyScheduleDetailInner, UserPermission.Supervisor);
